import { apiClient } from '@/lib/api-client';
import type { BillingInterval, CatalogStatus, FeatureLimits, Plan, Product } from '@/types/api';

export const adminCatalogKey = ['admin-catalog'] as const;

export interface ProductInput {
  name: string;
  slug?: string;
  description?: string;
  sort_order?: number;
}

export interface PlanInput {
  product_id: string;
  name: string;
  slug?: string;
  description?: string;
  billing_interval: BillingInterval;
  /** Minor units — 2900 is £29.00. The API rejects anything else. */
  price_minor: number;
  currency: string;
  /** -1 for unlimited. Zero is refused: a plan allowing no activations is unusable. */
  trial_days?: number;
  features?: FeatureLimits;
  sort_order?: number;
}

/**
 * The catalog, from the operator's side.
 *
 * These endpoints existed and were tested from the day the module shipped; only the screens
 * were missing, so the catalog could be read in the UI and only changed over the API. That is
 * a fine state for a module nobody has to use yet, and an untenable one for the first thing an
 * operator must do before any customer can use the platform at all.
 */
export const adminCatalogApi = {
  list: () => apiClient.get<Product[]>('/admin/catalog/products'),

  createProduct: (input: ProductInput) =>
    apiClient.post<Product>('/admin/catalog/products', input),

  updateProduct: (id: string, input: ProductInput) =>
    apiClient.patch<Product>(`/admin/catalog/products/${id}`, input),

  /**
   * Publishing is a separate call from editing, on the server as well as here.
   *
   * A subscription can only be sold against an *active* plan, so making something live is a
   * commercial act rather than a field change — and one an operator should have to mean.
   */
  setProductStatus: (id: string, status: CatalogStatus) =>
    apiClient.post<Product>(`/admin/catalog/products/${id}/status`, { status }),

  createPlan: (input: PlanInput) => apiClient.post<Plan>('/admin/catalog/plans', input),

  updatePlan: (id: string, input: PlanInput) =>
    apiClient.patch<Plan>(`/admin/catalog/plans/${id}`, input),

  setPlanStatus: (id: string, status: CatalogStatus) =>
    apiClient.post<Plan>(`/admin/catalog/plans/${id}/status`, { status }),
};

/**
 * Money in, as minor units.
 *
 * The API takes an integer of minor units and rejects anything else, but nobody types
 * "2900" when they mean £29. This parses what a person writes — "29", "29.00", "£29.00",
 * "29,00" — and returns pence, or null if it cannot be read.
 *
 * Returning null rather than 0 on failure is the point: 0 is a valid price (a free plan),
 * so a parser that fell back to it would silently publish a paid plan for nothing.
 */
export function parseMoneyToMinor(input: string): number | null {
  const cleaned = input.trim().replace(/[^\d.,-]/g, '').replace(',', '.');

  if (cleaned === '' || !/^\d+(\.\d{0,2})?$/.test(cleaned)) {
    return null;
  }

  return Math.round(Number.parseFloat(cleaned) * 100);
}

/** Minor units back to something editable — 2900 becomes "29.00". */
export function formatMinorForInput(minor: number): string {
  return (minor / 100).toFixed(2);
}
